import { CSSProperties, ReactNode } from 'react';

import PAGES from '@/constants/pages';

import { ExtendedCarItemType } from './cars.interfaces';
import { IExtendedGrid, IExtendedGridItem } from './grid.interfaces';

export type PageBuilderContextType = {
  children: ReactNode;
};

/**
 * Ключ страницы используемый в конструкторе
 */
export type PageType = (typeof PAGES)[keyof typeof PAGES];

export enum ConstructorBlocksEnum {
  CarHeader = 'car-header',
  CarsHeader = 'cars-header',
  Catalog = 'catalog',
  Filters = 'filters',
  ModelList = 'model-list',
  CarList = 'car-list',
  CarTile = 'car-tile',
  CarCompactTile = 'car-compact-tile',
  CarCard = 'car-card',
  Finance = 'finance',
  DealerMap = 'dealer-map',
  Benefits = 'benefits',
  Equipments = 'equipments',
  EquipmentsCustom = 'equipments-custom',
  BenefitsFootnote = 'benefits-footnote',
  BookingRequest = 'booking-request',
  DealerComment = 'dealer-comment',
  FavoritesHeader = 'favorites-header',
  FavoritesList = 'favorites-list',
  ComparisonHeader = 'comparison-header',
  ComparisonFooter = 'comparison-footer',
  ComparisonList = 'comparison-list',
  ComparisonSpecifications = 'comparison-specifications',
  ComparisonEquipments = 'comparison-equipments',
  Button = 'button',
  Custom = 'custom',
}

export enum CarTileBlocksEnum {
  CarTileHeader = 'car-tile-header',
  CarTileImages = 'car-tile-images',
  CarTileSpecification = 'car-tile-specification',
  CarTilePrice = 'car-tile-price',
  CarTilePayment = 'car-tile-payment',
  CarTileLocation = 'car-tile-location',
  CarTileTags = 'car-tile-tags',
  CarTileStatus = 'car-tile-status',
  CarTileActions = 'car-tile-actions',
  CarTileGallery = 'car-tile-gallery',
}

export enum CarCardBlocksEnum {
  CarCardImages = 'car-card-images',
  CarCardSpecification = 'car-card-specification',
  CarCardLocation = 'car-card-location',
  CarCardPrice = 'car-card-price',
  CarCardPayment = 'car-card-payment',
  CarCardMarkers = 'car-card-markers',
  CarCardTitle = 'car-card-title',
  CarCardBooking = 'car-card-booking',
  CarCardActions = 'car-card-actions',
}

export enum FilterBlocksEnum {
  FilterLocation = 'filter-location',
  FilterSelect = 'filter-select',
  FilterMultipleSelect = 'filter-multiple-select',
  FilterCheckboxGroup = 'filter-checkbox-group',
  FilterModelCheckboxGroup = 'filter-model-checkbox-group',
  FilterColorPicker = 'filter-color-picker',
  FilterDuoColorPicker = 'filter-duo-color-picker',
  FilterSlider = 'filter-slider',
  FilterRangeSlider = 'filter-range-slider',
}

export const ConstructorEnum = {
  ...ConstructorBlocksEnum,
  ...CarTileBlocksEnum,
  ...CarCardBlocksEnum,
  ...FilterBlocksEnum,
};

/**
 * Список типов блоков, на основе которых строится приложение
 */
export type ConstructorBlocksType =
  | ConstructorBlocksEnum
  | CarTileBlocksEnum
  | CarCardBlocksEnum
  | FilterBlocksEnum;

/**
 * Элемент конструктора для построения Grid
 */
export interface IConstructorGridItem<
  Type extends ConstructorBlocksType = ConstructorBlocksEnum,
> extends IExtendedGridItem {
  type: Type;
  /**
   * Флаг, отвечающий за отрисовку элемент в контейнере
   */
  container?: boolean;
  blockStyle?: CSSProperties;
  /**
   * Набор свойств, которые будут переданны компоненту
   */
  props?: Record<string, unknown>;
  /**
   * Конфигурация полей объекта автомобиля, используемых блоками, расположенными на странице автомобиля.
   */
  fields?: string[];
  /**
   * Функция, для условного пропуска отрисовки элемента сетки
   */
  skipRenderFn?: (carItem?: ExtendedCarItemType) => boolean;
}

/**
 * Карточка в плитке авто на странице автомобилей
 */
export type CarTileGridItemType = IConstructorGridItem<CarTileBlocksEnum>;

export type CarTileGridType = IExtendedGrid<CarTileGridItemType>;

/**
 * Карточка авто на странице автомобиля
 */
export type CarCardGridItemType = IConstructorGridItem<CarCardBlocksEnum>;

export type CarCardGridType = IExtendedGrid<CarCardGridItemType>;

/**
 * Блок фильтра
 */
export interface IFilterGridItem extends IConstructorGridItem<FilterBlocksEnum> {
  /**
   * Уникальное название поля для фильтрации.
   * Служит идентификатором для работы с фильтрами.
   */
  key: string;
  /**
   * Путь до поля в объекте `IExtendedCarData`.
   * - field - поле в объекте
   * - nestedField - поле в `ReferenceType` из `field` в объекте
   * - value - значение `nestedField` в формате JSON
   * @example
   * `field[nestedField='value']`
   *
   * - TODO: deprecated
   * После поля объекта можно указать коллекцию,
   * если поле, по которому идет фильтрация, является `ReferenceType`,
   * разделителем в этом случае служит знак `.`.
   * @example
   * `locations.dealers_cities`
   */
  path: string;
  /**
   * Тип отображения фильтра
   */
  type: FilterBlocksEnum;
  /**
   * Идентификаторы фильтров ({@link key}) от которого зависит данный фильтр
   */
  dependencies?: string[];
  /**
   * Форматер текста, который отображается,
   * если зависимости не были выполнены
   */
  dependencyTextFormatter?: (dependentFilterItems: IFilterGridItem[]) => string;
  /**
   * Идентификатор фильтра ({@link key}), по которым группируются значения
   */
  groupByKey?: string;
  /**
   * Флаг, позволяющий скрывать элемент фильтра, если в нем присутствует
   * только одна опция выбора.
   * Фильтр будет отображаться, если от него зависят другие фильтры.
   * Если фильтр входил в группу фильтров каждый из которых определял hideSingleOption: true
   * и они все имели по одному значению. То такая группа будет так же скрыта целиком.
   */
  hideSingleOption?: boolean;
  /**
   * При установке этого флага в true, у фильтра будет происходить
   * автоматическая установка значения, если оно одно единственное.
   *
   * Автоматическая установка фильтров происходит следующим образом:
   * 1) Определяется первый фильтр, подходящий для установки значения
   * 2) Устанавливается значение в фильтр
   * 3) Происходит запрос на сервер, за обновленными значениями фильтров
   * 4) Алгоритм повторяется, начиная с шага 1, так до тех пор, пока все
   *    значения для подходящих фильтров не будут установлены
   *
   * Этот алгоритм следует учитывать при указании данного флага у фильтров.
   * Если таких фильтров много, то пользователь может столкнуться с существенными задержками
   * связанными с запросами на сервер.
   */
  autoSelectSingleOption?: boolean;
  /**
   * Массив идентификаторов фильтров, от изменения которых, сбрасывается данный фильтр.
   */
  resetOnChange?: string[];
}

/**
 * Дополнительные свойства для группы фильтров
 */
export interface IFilterGridProperties {
  title: string;
  hidden?: boolean;
}

/**
 * Дополнительные свойства элемента фильтра
 */
export interface IFilterGridItemProperties {
  hidden?: boolean;
}

/**
 * Описывает грид фильтров.
 */
export type FilterGridType = Omit<
  IExtendedGrid<IFilterGridItem, IFilterGridProperties>,
  'items'
> & {
  items: IFilterGridItem[];
};

export type FilterRootGridType = Omit<ConstructorGridType, 'items'> & {
  items: FilterGridType[];
};

export interface IConstructorGridProperties {
  type?: ConstructorBlocksType;
  /**
   * Флаг, отвечающий за отрисовку элемент в контейнере
   */
  container?: boolean;
  /**
   * Конфигурация полей объекта автомобиля, используемых блоками, расположенными на странице автомобиля.
   */
  fields?: string[];
  /**
   * Набор свойств, которые будут переданны компоненту
   */
  props?: Record<string, unknown>;
}

/**
 * Совокупность возможных элементов `Grid`, используемых в `Constructor`
 */
export type PossibleConstructorGridItemType =
  | IConstructorGridItem
  | CarTileGridItemType
  | CarCardGridItemType
  | IFilterGridItem;

/**
 * Совокупность возможных свойств `Grid`, используемых в `Constructor`
 */
export type PossibleConstructorPropertiesType = Partial<
  IConstructorGridProperties & IFilterGridProperties
>;

export type ConstructorGridType<
  Item extends PossibleConstructorGridItemType = PossibleConstructorGridItemType,
  Properties extends
    PossibleConstructorPropertiesType = PossibleConstructorPropertiesType,
> = IExtendedGrid<Item, Properties>;

export type ConstructorElementType =
  | ConstructorGridType
  | PossibleConstructorGridItemType;

export type ConstructorGridItemsType = Array<ConstructorElementType>;

export type ConstructorType = Record<string, ConstructorGridType>;

export type ConstructorBlockType<T> = T;
